// ISO 6346 shipping container number validator.
// Ported from the Python ContainerValidator (originally by Sameer Shemna).
//
// A valid container number is 11 characters: 3 owner letters + product group
// (U|J|Z) + 6 registration digits + 1 check digit.

const ALPHABET_NUMERICAL: Record<string, number> = {
  A: 10, B: 12, C: 13, D: 14, E: 15, F: 16, G: 17,
  H: 18, I: 19, J: 20, K: 21, L: 23, M: 24, N: 25,
  O: 26, P: 27, Q: 28, R: 29, S: 30, T: 31, U: 32,
  V: 34, W: 35, X: 36, Y: 37, Z: 38,
};

const PATTERN = /^([A-Z]{3})(U|J|Z)(\d{6})(\d)$/;
const PATTERN_WITHOUT_CHECK_DIGIT = /^([A-Z]{3})(U|J|Z)(\d{6})$/;

// Loose variants accept any uppercase letter at position 4 (matching legacy
// cms.js chkDigit, which validates 4 letters + 7 digits without enforcing the
// ISO 6346 U/J/Z product-group restriction). Owner is treated as all 4 letters.
const PATTERN_LOOSE = /^([A-Z]{4})(\d{6})(\d)$/;
const PATTERN_LOOSE_WITHOUT_CHECK_DIGIT = /^([A-Z]{4})(\d{6})$/;

function buildCheckDigitFromLetters(letters: string, registration: string): number {
  const numerical: number[] = [];
  for (const c of letters) numerical.push(ALPHABET_NUMERICAL[c]);
  for (const d of registration) numerical.push(parseInt(d, 10));

  let sum = 0;
  for (let i = 0; i < numerical.length; i++) {
    sum += numerical[i] * Math.pow(2, i);
  }
  const checkDigit = sum % 11;
  return checkDigit === 10 ? 0 : checkDigit;
}

export interface ContainerValidationResult {
  valid: boolean;
  error?: string;
}

export interface ContainerValidationOptions {
  // When true (default), require the 4th character to be U, J, or Z per the
  // ISO 6346 product-group rule. When false, accept any uppercase letter at
  // that position — matches legacy cms.js chkDigit and is the right call for
  // surfaces that need to accept historical non-compliant container numbers.
  strict?: boolean;
}

export function validateContainerNumber(
  value: string,
  options: ContainerValidationOptions = {},
): ContainerValidationResult {
  if (!value) return { valid: false, error: 'Container number is required' };
  const strict = options.strict !== false;
  const upper = value.toUpperCase();

  if (strict) {
    const match = upper.match(PATTERN);
    if (!match) {
      return {
        valid: false,
        error: 'Format must be 3 letters + U/J/Z + 6 digits + check digit',
      };
    }
    const [, owner, group, registration, checkStr] = match;
    const expected = buildCheckDigitFromLetters(owner + group, registration);
    const actual = parseInt(checkStr, 10);
    if (actual !== expected) {
      return { valid: false, error: `Check digit mismatch (expected ${expected})` };
    }
    return { valid: true };
  }

  const match = upper.match(PATTERN_LOOSE);
  if (!match) {
    return {
      valid: false,
      error: 'Format must be 4 letters followed by 7 digits',
    };
  }
  const [, letters, registration, checkStr] = match;
  const expected = buildCheckDigitFromLetters(letters, registration);
  const actual = parseInt(checkStr, 10);
  if (actual !== expected) {
    return { valid: false, error: `Check digit mismatch (expected ${expected})` };
  }
  return { valid: true };
}

export function isValidContainerNumber(
  value: string,
  options?: ContainerValidationOptions,
): boolean {
  return validateContainerNumber(value, options).valid;
}

// Save-time required-field check. The container number is required only for
// IMPORT orders; every other movetype may legitimately save with no container
// (it's populated later in the workflow, or never at all for some lanes).
// Returns null when the requirement is satisfied or doesn't apply; otherwise
// returns the message to surface to the user.
export function validateContainerRequired(
  moveType: string | null | undefined,
  value: string | null | undefined,
): string | null {
  if (moveType !== 'IMPORT') return null;
  if ((value ?? '').trim() === '') return 'Container number is required for IMPORT orders';
  return null;
}

// Compute the check digit for a container prefix (no check digit). Returns -1
// if the input doesn't match the format. `strict` defaults to true for
// backward compatibility.
export function createCheckDigit(
  value: string,
  options: ContainerValidationOptions = {},
): number {
  if (!value) return -1;
  const strict = options.strict !== false;
  const upper = value.toUpperCase();
  if (strict) {
    const match = upper.match(PATTERN_WITHOUT_CHECK_DIGIT);
    if (!match) return -1;
    const [, owner, group, registration] = match;
    return buildCheckDigitFromLetters(owner + group, registration);
  }
  const match = upper.match(PATTERN_LOOSE_WITHOUT_CHECK_DIGIT);
  if (!match) return -1;
  const [, letters, registration] = match;
  return buildCheckDigitFromLetters(letters, registration);
}
